import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  CheckCircle2, AlertTriangle, XCircle, Clock, Info, ChevronRight, ExternalLink,
  Activity, Settings2, History, BarChart2, ShieldAlert, RefreshCw, Eye, EyeOff, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { relativeTime, fmtCurrency, fmtNumber } from "@/lib/utils";

const STATUS_META = {
  connected: { icon: CheckCircle2, label: "Connected", className: "text-signal-500" },
  degraded: { icon: AlertTriangle, label: "Degraded", className: "text-amber-accent" },
  invalid_credentials: { icon: ShieldAlert, label: "Invalid credentials", className: "text-destructive" },
  rate_limited: { icon: AlertTriangle, label: "Rate limited", className: "text-amber-accent" },
  quota_exceeded: { icon: AlertTriangle, label: "Quota exceeded", className: "text-amber-accent" },
  disconnected: { icon: XCircle, label: "Disconnected", className: "text-destructive" },
  not_configured: { icon: XCircle, label: "Not configured", className: "text-destructive" },
  coming_soon: { icon: Clock, label: "Phase 2", className: "text-amber-accent" },
};

const SEVERITY_META = {
  critical: { icon: XCircle, className: "border-destructive/40 bg-destructive/10 text-destructive" },
  warning: { icon: AlertTriangle, className: "border-amber-accent/40 bg-amber-accent/10 text-amber-accent" },
  info: { icon: Info, className: "border-signal-500/40 bg-signal-500/10 text-signal-500" },
};

export default function IntegrationsPage() {
  const [data, setData] = useState(null);
  const [verifying, setVerifying] = useState({});
  const [openDetail, setOpenDetail] = useState(null);

  const load = () => api.get("/integrations").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const verify = async (integ) => {
    setVerifying((v) => ({ ...v, [integ.id]: true }));
    try {
      const { data: res } = await api.post(`/integrations/${integ.id}/verify`);
      const m = STATUS_META[res.status];
      if (res.status === "connected") toast.success(`${integ.name}: ${m?.label || res.status}`);
      else toast.warning(`${integ.name}: ${m?.label || res.status} — ${res.message || ""}`);
      load();
    } catch (e) { toast.error("Verify failed"); }
    setVerifying((v) => ({ ...v, [integ.id]: false }));
  };

  const verifyAll = async () => {
    const live = data.integrations.filter((i) => i.supports_verify);
    toast.info(`Verifying ${live.length} connectors...`);
    for (const i of live) await verify(i);
  };

  const toggle = async (integ, next) => {
    try {
      await api.patch(`/integrations/${integ.id}`, { enabled: next });
      toast.success(`${integ.name} ${next ? "enabled" : "disabled"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
  };

  if (!data) return <DashboardLayout title="..."><div className="text-sm text-muted-foreground">Loading...</div></DashboardLayout>;

  const groups = data.integrations.reduce((acc, i) => {
    (acc[i.category] = acc[i.category] || []).push(i); return acc;
  }, {});

  return (
    <DashboardLayout
      eyebrow="Connectors & APIs"
      title="Integration Operations"
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={verifyAll} data-testid="integ-verify-all"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Verify all</Button>
          <Link to="/app/help" data-testid="integrations-help-link" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">Setup docs <ChevronRight className="w-3 h-3" /></Link>
        </div>
      }
    >
      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 grid-borders bg-card/30 mb-6">
        <Stat label="Total" value={data.summary.total} testid="integ-stat-total" />
        <Stat label="Connected" value={data.summary.connected} tone="signal" testid="integ-stat-connected" />
        <Stat label="Degraded" value={data.summary.degraded} tone="amber" testid="integ-stat-degraded" />
        <Stat label="Disconnected" value={data.summary.disconnected} tone="destructive" testid="integ-stat-disconnected" />
        <Stat label="Active here" value={data.summary.active_in_workspace} testid="integ-stat-active" />
      </div>

      {data.alerts.length > 0 && (
        <div className="space-y-2 mb-6" data-testid="integ-alerts">
          {data.alerts.map((a, i) => {
            const m = SEVERITY_META[a.severity] || SEVERITY_META.info;
            const Icon = m.icon;
            return (
              <div key={i} className={`px-4 py-3 rounded-sm border text-sm flex items-start gap-3 ${m.className}`}>
                <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div className="flex-1"><div className="font-medium">{a.name}</div><div className="text-xs opacity-90">{a.message}</div></div>
                <span className="text-[0.6rem] uppercase tracking-widest font-mono">{a.severity}</span>
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-8">
        {Object.entries(groups).map(([cat, items]) => (
          <section key={cat}>
            <div className="label-eyebrow mb-3">{cat}</div>
            <div className="border border-border rounded-sm overflow-hidden bg-card/20">
              {items.map((i, idx) => {
                const sm = STATUS_META[i.status] || STATUS_META.not_configured;
                const SIcon = sm.icon;
                return (
                  <div key={i.id} data-testid={`integ-row-${i.id}`}
                    className={`grid grid-cols-12 gap-3 px-5 py-4 items-center ${idx > 0 ? "border-t border-border" : ""}`}>
                    <div className="col-span-12 md:col-span-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-semibold tracking-tight">{i.name}</span>
                        {i.phase === 1 && <span className="text-[0.55rem] tracking-widest uppercase font-mono px-1.5 py-0.5 border border-border rounded-sm">MVP</span>}
                        {!i.plan_allowed && <span className="text-[0.55rem] tracking-widest uppercase font-mono px-1.5 py-0.5 border border-amber-accent/40 text-amber-accent rounded-sm inline-flex items-center gap-1"><Lock className="w-2.5 h-2.5" />upgrade</span>}
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{i.description}</p>
                    </div>

                    <div className="col-span-6 md:col-span-3">
                      <div className={`flex items-center gap-1.5 text-xs ${sm.className}`}>
                        <SIcon className="w-3.5 h-3.5" />
                        <span className="font-medium">{sm.label}</span>
                      </div>
                      {i.last_check && (
                        <div className="mt-1 text-[0.65rem] text-muted-foreground">
                          checked {relativeTime(i.last_check.checked_at)}
                          {i.last_check.latency_ms != null && <> · {i.last_check.latency_ms}ms</>}
                        </div>
                      )}
                      {i.missing_env?.length > 0 && !i.last_check && (
                        <div className="mt-1 text-[0.65rem] text-muted-foreground font-mono">missing: {i.missing_env.join(", ")}</div>
                      )}
                    </div>

                    <div className="col-span-3 md:col-span-2 flex items-center gap-2">
                      {i.supports_verify && (
                        <Button size="sm" variant="outline" onClick={() => verify(i)} disabled={verifying[i.id]} data-testid={`integ-verify-${i.id}`}>
                          <RefreshCw className={`w-3 h-3 mr-1 ${verifying[i.id] ? "animate-spin" : ""}`} />Verify
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setOpenDetail(i)} data-testid={`integ-detail-${i.id}`}>
                        <Settings2 className="w-3 h-3 mr-1" />Manage
                      </Button>
                    </div>

                    <div className="col-span-3 md:col-span-3 flex items-center justify-end gap-3">
                      {i.toggleable ? (
                        <>
                          <span className="text-[0.65rem] uppercase tracking-widest text-muted-foreground hidden sm:inline">{i.enabled ? "On" : "Off"}</span>
                          <Switch checked={i.enabled} onCheckedChange={(v) => toggle(i, v)} data-testid={`integ-toggle-${i.id}`} disabled={!i.plan_allowed} />
                        </>
                      ) : (
                        <span className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">{i.status === "coming_soon" ? "Phase 2" : "—"}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <DetailDialog integ={openDetail} onClose={() => setOpenDetail(null)} onChanged={load} />

      <div className="mt-10 border-t border-border pt-6 text-xs text-muted-foreground flex flex-wrap items-center gap-2">
        <ExternalLink className="w-3 h-3" />
        <span>Live status uses workspace credentials first, falls back to <span className="font-mono">/app/backend/.env</span>. See</span>
        <Link to="/app/help" className="text-foreground underline">Help &amp; Setup</Link>
      </div>
    </DashboardLayout>
  );
}

function Stat({ label, value, sub, testid, tone }) {
  const valColor = tone === "signal" ? "text-signal-500" : tone === "amber" ? "text-amber-accent" : tone === "destructive" ? "text-destructive" : "";
  return (
    <div className="p-5" data-testid={testid}>
      <div className="label-eyebrow mb-2">{label}</div>
      <div className={`text-3xl font-display font-semibold tracking-tightest ${valColor}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function DetailDialog({ integ, onClose, onChanged }) {
  const [history, setHistory] = useState([]);
  const [usage, setUsage] = useState(null);
  const [creds, setCreds] = useState({ credentials: [], required_env: [] });
  const [credValues, setCredValues] = useState({});
  const [show, setShow] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!integ) return;
    Promise.all([
      api.get(`/integrations/${integ.id}/history?limit=10`),
      api.get(`/integrations/${integ.id}/usage`).catch(() => ({ data: null })),
      api.get(`/integrations/${integ.id}/credentials`),
    ]).then(([h, u, c]) => {
      setHistory(h.data.history || []);
      setUsage(u.data);
      setCreds(c.data);
      setCredValues({});
    });
  }, [integ]);

  if (!integ) return null;

  const saveCreds = async () => {
    setBusy(true);
    try {
      await api.put(`/integrations/${integ.id}/credentials`, { credentials: credValues });
      toast.success("Credentials saved");
      const c = await api.get(`/integrations/${integ.id}/credentials`);
      setCreds(c.data); setCredValues({});
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const clearCred = async (env_key) => {
    if (!window.confirm(`Clear workspace value for ${env_key}? Will fall back to global .env.`)) return;
    await api.delete(`/integrations/${integ.id}/credentials/${env_key}`);
    const c = await api.get(`/integrations/${integ.id}/credentials`);
    setCreds(c.data);
    onChanged && onChanged();
    toast.success("Cleared");
  };

  return (
    <Dialog open={!!integ} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{integ.name}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{integ.description}</p>

        <Tabs defaultValue="configure" className="mt-2">
          <TabsList>
            <TabsTrigger value="configure" data-testid="integ-tab-configure"><Settings2 className="w-3 h-3 mr-1.5" />Configure</TabsTrigger>
            <TabsTrigger value="history" data-testid="integ-tab-history"><History className="w-3 h-3 mr-1.5" />History</TabsTrigger>
            <TabsTrigger value="usage" data-testid="integ-tab-usage"><BarChart2 className="w-3 h-3 mr-1.5" />Usage</TabsTrigger>
          </TabsList>

          <TabsContent value="configure" className="space-y-4 py-3">
            <p className="text-xs text-muted-foreground">Per-workspace credentials override the global <span className="font-mono">.env</span>. Stored Fernet-encrypted.</p>
            {(creds.credentials || []).map((c) => (
              <div key={c.env_key} className="border border-border rounded-sm p-3 bg-card/30">
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-mono text-xs">{c.env_key}</Label>
                  <span className={`text-[0.6rem] tracking-widest uppercase ${c.source === "workspace" ? "text-signal-500" : c.source === "env" ? "text-amber-accent" : "text-destructive"}`}>{c.source}</span>
                </div>
                <div className="flex gap-2 items-center">
                  <Input
                    type={show[c.env_key] ? "text" : "password"}
                    placeholder={c.preview || "Not set"}
                    value={credValues[c.env_key] ?? ""}
                    onChange={(e) => setCredValues({ ...credValues, [c.env_key]: e.target.value })}
                    data-testid={`integ-cred-${c.env_key}`}
                  />
                  <Button size="sm" variant="outline" onClick={() => setShow({ ...show, [c.env_key]: !show[c.env_key] })}>
                    {show[c.env_key] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </Button>
                  {c.source === "workspace" && (
                    <Button size="sm" variant="ghost" onClick={() => clearCred(c.env_key)} data-testid={`integ-clear-${c.env_key}`}>Clear</Button>
                  )}
                </div>
                {c.preview && <div className="mt-1 text-[0.65rem] text-muted-foreground font-mono">current: {c.preview}</div>}
              </div>
            ))}
            {(creds.credentials || []).length === 0 && <p className="text-xs text-muted-foreground">No credentials required.</p>}

            <DialogFooter>
              <Button onClick={saveCreds} disabled={busy || Object.values(credValues).every((v) => !v)} data-testid="integ-save-creds">{busy ? "Saving..." : "Save credentials"}</Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="history" className="py-3">
            <div className="space-y-2">
              {history.length === 0 && <p className="text-xs text-muted-foreground">No verification runs yet.</p>}
              {history.map((h, i) => {
                const m = STATUS_META[h.status] || STATUS_META.not_configured;
                return (
                  <div key={i} className="border border-border rounded-sm p-3 text-xs" data-testid={`integ-history-${i}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-medium inline-flex items-center gap-1.5 ${m.className}`}><m.icon className="w-3 h-3" />{m.label}</span>
                      <span className="text-muted-foreground">{relativeTime(h.checked_at)}{h.latency_ms != null && <> · {h.latency_ms}ms</>}</span>
                    </div>
                    {h.message && <div className="text-muted-foreground">{h.message}</div>}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="usage" className="py-3 space-y-3">
            {usage ? (
              <>
                {usage.runs !== undefined && (
                  <div className="grid grid-cols-3 grid-borders bg-card/30">
                    <Stat label="Agent runs" value={fmtNumber(usage.runs)} />
                    <Stat label="Est. tokens" value={fmtNumber(usage.est_tokens || 0)} />
                    <Stat label="Est. cost" value={fmtCurrency(usage.est_cost_usd || 0)} />
                  </div>
                )}
                {usage.transactions !== undefined && (
                  <div className="grid grid-cols-2 grid-borders bg-card/30">
                    <Stat label="Total checkouts" value={fmtNumber(usage.transactions)} />
                    <Stat label="Paid" value={fmtNumber(usage.paid)} />
                  </div>
                )}
                {(usage.by_agent || []).length > 0 && (
                  <div>
                    <div className="label-eyebrow mb-2">By agent</div>
                    <ul className="text-xs space-y-1">
                      {usage.by_agent.map((b) => (
                        <li key={b.agent_type} className="flex items-center justify-between border-b border-border py-1">
                          <span className="capitalize">{b.agent_type}</span>
                          <span className="font-mono">{b.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {usage.note && <p className="text-xs text-muted-foreground">{usage.note}</p>}
              </>
            ) : <p className="text-xs text-muted-foreground">No usage data.</p>}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
